import React, { useEffect, useState } from 'react';

import Accordion from '../accordion';
import CustomButton from '../customButton';
import MultiSelector from '../multiSelector';

import utils from '../../utils';
import API from '../../services/api';

import './styles.scss';
import { useSelector } from 'react-redux';
import { getSelectedProject } from '../../redux/projects/actions';
import reportUtils from '../../utils/report';
import { getCmsInfo } from '../../utils/integrations';
import { getIntegrations } from '../../redux/integration/actions';

const AnalyticsSelector = ({ campaigns, selectedDashboard, sites, appliedFilters, onSelection, cmsIntegration }) => {
  const [medias, setMedias] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [isLoadingMedias, setIsLoadingMedias] = useState(false);
  const [isLoadingAdvertisers, setIsLoadingAdvertisers] = useState(false);

  const [localFilters, setLocalFilters] = useState({
    // availableSites: full list of devices displayed in the selector
    // selectedSites: subset of devices currently selected by the user
    availableSites: sites,
    selectedSites: sites,
    selectedCampaigns: campaigns,
    campaigns: campaigns.map(campaign => campaign.id),
    selectedAdvertisers: [],
    selectedMedias: []
  });

  const [cms, setCms] = useState(null);

  const project = useSelector(getSelectedProject);
  const integrations = useSelector(getIntegrations);

  useEffect(() => {
    // keep localFilters in sync when parent devices/sites props change (page load or project change)
    setLocalFilters(prev => ({
      ...prev,
      availableSites: sites || [],
      // if there was no previous explicit selection, default selected to all available
      selectedSites: prev.selectedSites && prev.selectedSites.length > 0 ? prev.selectedSites : sites || [],
      selectedCampaigns: campaigns || prev.selectedCampaigns,
      campaigns: (campaigns && campaigns.map(campaign => campaign.id)) || prev.campaigns
    }));

    if (cmsIntegration) {
      const cmsCandidate = getCmsInfo(cmsIntegration, integrations);
      setCms(cmsCandidate);
    } else {
      // Clear media and advertiser data if current solution is not
      setCms(null);
      mediaCallback([]);
      advertisersCallback([]);
    }
  }, [sites, campaigns, cmsIntegration, integrations, selectedDashboard]);

  useEffect(() => {
    if (cms) {
      setIsLoadingAdvertisers(true);
      setIsLoadingMedias(true);
      fetchCMSFilters('advertisers', cms.sourceTable, cms.popColumnMappings, advertisersCallback);
    }
  }, [cms, appliedFilters.startDate, appliedFilters.startDate]);

  useEffect(() => {
    if (cms) {
      fetchCMSFilters('medias', cms.sourceTable, cms.popColumnMappings, mediaCallback);
    }
  }, [advertisers]);

  const isButtonDisabled = (previousItems, currentItems) => {
    const prevIds = Array.isArray(previousItems)
      ? previousItems.map(item => (item && item.id !== undefined ? item.id : item))
      : [];
    const currIds = Array.isArray(currentItems)
      ? currentItems.map(item => (item && item.id !== undefined ? item.id : item))
      : [];
    return currIds.length === 0 || utils.compareArrays(prevIds, currIds);
  };

  const mediaCallback = newMedias => {
    onSelection({ selectedMedias: newMedias });
    setMedias(newMedias);
    setLocalFilters(prev => ({ ...prev, selectedMedias: newMedias }));
    setIsLoadingMedias(false);
  };

  const advertisersCallback = newAdvertisers => {
    onSelection({ selectedAdvertisers: newAdvertisers });
    setAdvertisers(newAdvertisers);
    setLocalFilters(prev => ({ ...prev, selectedAdvertisers: newAdvertisers }));
    setIsLoadingAdvertisers(false);
  };

  const fetchCMSFilters = (fieldName, table, columns, callback) => {
    const field = columns[fieldName];
    const data = reportUtils.generateCMSQueryObject(field, appliedFilters.startDate, appliedFilters.endDate);
    const localCampaigns = appliedFilters
      ? appliedFilters?.selectedAdvertisers?.map(advertiser => advertiser.name)
      : [];

    API.getCMSData(project.id, table, data, localCampaigns, appliedFilters.selectedDevices)
      .then(res => {
        const datasets = res.datasets;
        if (datasets && datasets.length > 0) {
          let cmsData = [];
          const datasetValue = datasets[0].value || [];
          if (typeof datasetValue === 'string') {
            cmsData = [{ id: `${fieldName}-0`, name: utils.escapeQuotes(datasetValue) }];
          } else {
            cmsData = datasetValue.sort(utils.compareStrings).map((value, index) => {
              return { id: `${fieldName}-${index}`, name: value };
            });
          }

          // TODO: Look for changes on CMS filter
          // utils.getListIntersection(this.state[selectedCMSData], cmsData, fieldName, this.props.setAlert)
          callback(cmsData);
        } else {
          callback([]);
        }
      })
      .catch(err => {
        console.error(err);
        this.onError(err);
      });
  };

  const populateSites = selectedCampaigns => {
    // Gather all sites from selected campaigns then dedupe by id
    const allSites = selectedCampaigns.reduce((acc, campaign) => acc.concat(campaign.sites || []), []);
    const uniqueSitesMap = new Map();
    allSites.forEach(site => {
      if (site && site.id && !uniqueSitesMap.has(site.id)) uniqueSitesMap.set(site.id, site);
    });
    const uniqueSites = Array.from(uniqueSitesMap.values());

    // Update local available sites and default selection to all unique sites
    setLocalFilters(prev => ({
      ...prev,
      availableSites: uniqueSites,
      selectedSites: uniqueSites,
      sites: uniqueSites.map(site => site.id),
      selectedCampaigns: selectedCampaigns
    }));
    // Do NOT notify parent here; parent will be updated when the user clicks "Update Analytics"
  };

  /*
  const renderActionButtons = campaign => {
    const styles = `action-buttons ${campaignQuickView === campaign.id ? 'is-active' : ''}`;
    return (
      <>
        <div className={styles}>
          <ActionButtons
            buttons={[
              {
                id: `quick-data-${campaign.id}`,
                icon: campaignQuickView === campaign.id ? 'soloDark' : 'eyeDark',
                key: 'quick-data',
                tooltip: 'See the data',
                onPress: () => onQuickView(campaign)
              }
            ]}
          />
        </div>
      </>
    );
  };
    */

  let sections = [
    {
      header: `Campaigns (${
        appliedFilters?.selectedCampaigns?.length > 0 ? appliedFilters?.selectedCampaigns?.length : campaigns?.length
      }/${campaigns?.length})`,
      body: (
        <>
          <MultiSelector
            items={campaigns}
            onSelect={value => {
              populateSites(value);
            }}
            displayField="displayName"
            emptyMessage="No Campaigns found "
            customSearchPlaceHolder="Search Campaigns"
            sortField="displayName"
            // renderActionButtons={renderActionButtons}
          />
          {campaigns?.length > 0 && (
            <CustomButton
              handleClick={() => {
                onSelection(localFilters);
              }}
              classes="btn-primary btn-slim"
              title="Update Analytics"
              disabled={isButtonDisabled(appliedFilters?.selectedCampaigns, localFilters?.selectedCampaigns)}
            />
          )}
        </>
      )
    },
    {
      header: `Sites (${localFilters?.selectedSites?.length}/${localFilters?.availableSites?.length})`,
      body: (
        <>
          <MultiSelector
            // show the available devices for the currently selected sites
            items={localFilters?.availableSites}
            // when the user toggles selection, only update the selectedSites set
            onSelect={value => {
              const devicesForSelectedSites = [];
              campaigns.forEach(campaign => {
                const campaignDevices = Array.from(new Set(campaign.devices));
                value.forEach(site => {
                  campaignDevices.forEach(device => {
                    if (device.attachedSites.includes(site.id)) {
                      devicesForSelectedSites.push(device);
                    }
                  });
                });
              });
              const devices = Array.from(new Set(devicesForSelectedSites.map(device => device)));
              // Also update `sites` (array of site ids) so parent receives the
              // normalized site id list when `onSelection` is called.
              setLocalFilters(prev => ({
                ...prev,
                selectedSites: value,
                selectedDevices: devices,
                sites: value.map(site => site.id)
              }));
            }}
            displayField="displayName"
            emptyMessage={`No Sites found for selected Campaigns`}
            customSearchPlaceHolder={`Search Sites`}
            sortField="displayName"
          />
          {sites?.length > 0 && (
            <CustomButton
              handleClick={() => onSelection(localFilters)}
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedSites, localFilters?.selectedSites)}
            />
          )}
        </>
      )
    },
    {
      header: isLoadingAdvertisers
        ? 'Loading Advertisers...'
        : `Campaigns/Advertisers (${appliedFilters?.selectedAdvertisers?.length}/${advertisers?.length})`,
      body: (
        <>
          <MultiSelector
            items={advertisers}
            onSelect={value => {
              setLocalFilters({ ...localFilters, selectedAdvertisers: value });
            }}
            displayField="name"
            emptyMessage="No Campaigns found for this period"
            customSearchPlaceHolder="Search Campaign"
            sortField="name"
          />
          {advertisers.length > 0 && (
            <CustomButton
              handleClick={() => onSelection(localFilters)}
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedAdvertisers, localFilters?.selectedAdvertisers)}
            />
          )}
        </>
      )
    },
    {
      header: isLoadingMedias
        ? 'Loading Medias...'
        : `Media (${appliedFilters?.selectedMedias?.length}/${medias?.length})`,
      body: (
        <>
          <MultiSelector
            items={medias}
            onSelect={value => {
              setLocalFilters({ ...localFilters, selectedMedias: value });
            }}
            displayField="name"
            emptyMessage="No Medias found for this period"
            customSearchPlaceHolder="Search Media"
            sortField="name"
          />
          {medias.length > 0 && (
            <CustomButton
              handleClick={() => onSelection(localFilters)}
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedMedias, localFilters?.selectedMedias)}
            />
          )}
        </>
      )
    }
  ];

  if (!cmsIntegration) {
    // Remove Advertisers/Media section if CMS toggle is disabled
    sections = sections.slice(0, sections.length - 2);
  }

  return (
    <div className={`side-menu ${cmsIntegration ? 'cms-mode' : ''}`}>
      <Accordion items={sections}></Accordion>
    </div>
  );
};

export default AnalyticsSelector;
